import { useCallback, useEffect, useMemo } from 'react';

import { STATUS_CODE } from '@kc-console/core';

import { useStreamChannelStore } from '../stores/useStreamChannelStore';
import StreamClient from '../utils/StreamClient';
import useServiceAppState from './useServiceAppState';

interface UseStreamOptions {
  autoConnect?: boolean;
  streamUrl?: string;
}

interface StreamControls {
  connect: () => void;
  connectedCount: number;
  disconnect: () => void;
  isConnected: boolean;
}

const useGlobalStream = (options: UseStreamOptions = {}): StreamControls => {
  const { streamUrl, autoConnect = true } = options;

  const {
    state: { status, token },
    setClientAppStatus,
  } = useServiceAppState();
  const isUnauthorized = status === STATUS_CODE.UNAUTHORIZED_EXPIRED_TOKEN;

  const isConnected = useStreamChannelStore(state => state.isConnected);
  const connectedCount = useStreamChannelStore(state => state.count);
  const publish = useStreamChannelStore(state => state.publish);
  const setControls = useStreamChannelStore(state => state.setControls);
  const setCount = useStreamChannelStore(state => state.setCount);
  const setStreamConnected = useStreamChannelStore(state => state.setIsConnected);

  const client = useMemo(() => {
    return new StreamClient({
      onCountChange: setCount,
      onConnectionChange: setStreamConnected,
    });
  }, [setCount, setStreamConnected]);

  useEffect(() => {
    setCount(client.count);
  }, [client, setCount]);

  useEffect(() => {
    client.updateHandlers({
      onMessage: payload => {
        publish(payload);
      },
      onUnauthorized: () => {
        setClientAppStatus(STATUS_CODE.UNAUTHORIZED_EXPIRED_TOKEN);
      },
    });
  }, [client, publish, setClientAppStatus]);

  const disconnect = useCallback(() => {
    client.disconnect();
  }, [client]);

  const connect = useCallback(() => {
    if (isUnauthorized) return;
    client.connect(token, streamUrl);
  }, [client, isUnauthorized, streamUrl, token]);

  useEffect(() => {
    if (isUnauthorized) {
      disconnect();
      return;
    }

    if (!autoConnect) {
      return;
    }

    const reconnectIfNeeded = () => {
      if (document.visibilityState !== 'visible') return;
      if (!navigator.onLine) return;

      connect();
    };

    connect();

    window.addEventListener('focus', reconnectIfNeeded);
    window.addEventListener('online', reconnectIfNeeded);
    document.addEventListener('visibilitychange', reconnectIfNeeded);

    return () => {
      window.removeEventListener('focus', reconnectIfNeeded);
      window.removeEventListener('online', reconnectIfNeeded);
      document.removeEventListener('visibilitychange', reconnectIfNeeded);
      disconnect();
    };
  }, [autoConnect, connect, disconnect, isUnauthorized]);

  useEffect(() => {
    setControls(connect, disconnect);
  }, [connect, disconnect, setControls]);

  return {
    connect,
    disconnect,
    connectedCount,
    isConnected,
  };
};

export default useGlobalStream;
